// FM-04 — an unpinned external standard reference.
//
// The contract binds a party to a document without recording which version that
// was. VA and DOE general counsels reached opposite conclusions on a $68M
// question over whether Appendix 2 of a 2009 FEMP guide had been superseded by
// Appendix 3. Both were reading the contract correctly; the contract had not
// said which document it meant.
//
// This is the most checkable failure mode in the taxonomy: a citation either
// carries a version and a date, or it does not.

import type { Citation, Flag, FlagDraft, SourceDocument } from '../../types';
import { evidenceFor, detectionSignal, tierOf } from '../../data/taxonomy';
import { buildLocator, sectionRefOf } from './locate';
import { enrichFlag } from '../finding';

export const CHECK_ID = 'citation-check';

/** Language that defers to whatever was current without saying what that was. */
const DRIFT_PHRASE =
  /\b(?:latest version|most recent version|as amended|current(?:ly)?\s+(?:in\s+)?effect|in effect at the time of|then[- ]current|current guidance)\b/i;

export type UrlLiveness = 'live' | 'dead' | 'unresolved';

/**
 * URL liveness is HEAD-checked through the Worker, because the browser cannot
 * do it cross-origin. Until that Worker exists every URL is "unresolved" — which
 * is reported as unresolved, not as dead. Claiming a link is broken when we
 * simply have not looked would be the same sin the tool exists to catch.
 */
export type UrlResolver = (url: string) => Promise<UrlLiveness>;

export const stubResolver: UrlResolver = async () => 'unresolved';

function reasonsFor(citation: Citation, liveness: UrlLiveness): string[] {
  const reasons: string[] = [];
  if (citation.placeholder) {
    reasons.push('the reference is an unresolved placeholder');
  }
  if (!citation.versionGiven) reasons.push('no version number is given');
  if (!citation.dateGiven) reasons.push('no date is given');
  if (DRIFT_PHRASE.test(citation.raw)) {
    reasons.push('it defers to whichever version was current, unnamed');
  }
  if (citation.kind === 'url' && liveness === 'dead') {
    reasons.push('the target does not resolve');
  }
  if (citation.kind === 'url' && liveness === 'unresolved') {
    reasons.push('the target has not been checked for liveness');
  }
  return reasons;
}

function shouldFlag(citation: Citation): boolean {
  return (
    !citation.versionGiven ||
    citation.placeholder ||
    DRIFT_PHRASE.test(citation.raw)
  );
}

export async function citationCheck(
  doc: SourceDocument,
  resolve: UrlResolver = stubResolver,
): Promise<Flag[]> {
  const locator = buildLocator(doc);
  const flags: FlagDraft[] = [];
  let counter = 0;

  // One flag per failure mode per section: a plan citing the same unpinned
  // guideline in six places has one problem, not six.
  const seen = new Set<string>();

  for (const citation of doc.citations) {
    if (!shouldFlag(citation)) continue;

    const liveness: UrlLiveness =
      citation.kind === 'url' ? await resolve(citation.resolvedUrl ?? citation.raw) : 'unresolved';

    // Find the citation in the raw text so the quote is verbatim and the section
    // is the one it actually sits in.
    const index = doc.rawText.indexOf(citation.raw);
    const section = index >= 0 ? locator.sectionOf(index) : null;
    const ref = sectionRefOf(section);

    const key = `${ref}::${citation.kind}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const quote =
      index >= 0
        ? locator.quoteAround(index, citation.raw.length, 140)
        : null;

    const reasons = reasonsFor(citation, liveness);

    const missing: string[] = [];
    if (!citation.versionGiven) missing.push('version');
    if (!citation.dateGiven) missing.push('date');
    if (citation.placeholder || (citation.kind === 'url' && liveness !== 'live')) {
      missing.push('resolvable target');
    }

    flags.push({
      id: `flag-${CHECK_ID}-${++counter}`,
      failureModeId: 'FM-04',
      tier: tierOf('FM-04'),
      layer: 'deterministic',
      checkId: CHECK_ID,
      title: citation.placeholder
        ? 'An unresolved placeholder citation'
        : 'An external reference with no version pinned',
      quote,
      sectionRef: ref,
      page: citation.page,
      quoteOffsets: locator.offsetsOf(quote, index >= 0 ? index : undefined),
      basis: detectionSignal('FM-04', citation.placeholder ? 2 : 0),
      evidenceCaseIds: evidenceFor('FM-04'),
      receipt: {
        kind: 'resolution',
        inputs: [
          { label: 'Citation', value: citation.raw },
          { label: 'Page', value: String(citation.page) },
          { label: 'Kind', value: citation.kind },
          { label: 'Version given', value: citation.versionGiven ? 'yes' : 'no' },
          { label: 'Date given', value: citation.dateGiven ? 'yes' : 'no' },
          {
            label: 'Target',
            value:
              citation.kind === 'url'
                ? `${citation.resolvedUrl ?? citation.raw} — ${liveness}`
                : 'not a URL',
          },
        ],
        computation:
          'A citation is pinned when it names a version and a date and resolves to a retrievable document.',
        result:
          missing.length > 0
            ? `Missing: ${missing.join(', ')}. ${reasons.join('; ')}.`
            : `${reasons.join('; ')}.`,
      },
      status: 'open',
    });
  }

  return flags.map(enrichFlag);
}
